export interface ConfigLP {
  campagnaID: number;
  c__fowc2kq5: string;
  c__BYBIDFRZ: string;
  c__hyez2gj1: string;
  c__p4ft2h15?: string;  // tipo trattenuta
  c__3j8d94nv?: number;  // segnalatore
  user_photo?: string;
  nomeAgente?: string;
}

export enum ProfessioneEnum {
  dipendenteParapubblico = "Dipendente Parapubblico",
  dipendentePrivato = "Dipendente Privato",
  dipendentePrivatoGrande = "Dipendente Privato Grande",
  dipendentePubblico = "Dipendente Pubblico",
  dipendentePostale = "Dipendente Postale",
  dipendenteStatale = "Dipendente Statale",
  medicoConvenzionato = "Medico Convenzionato",
  pensionatoINPS = "Pensionato INPS",
  pensionatoAltro = "Pensionato Altro",
}

export interface PermissionItem {
  inputValue?: string;
  label: string;
  grant: string;
}

export interface Rules {
  uid?: string;
  name: string;
  campaign: number;
  cap_month: number;
  province_preferences: string[];
  active: boolean;
  distribution_channel: "hub" | "rete";
  distribution_weight: number;
  lead_received?: number;
  accept_out_preference: boolean;
}

export interface Org {
  uid?: string;
  ragioneSociale: string;
  displayName: string;
  active: boolean;
  vatNumber: string;
  firstName: string;
  lastName: string;
  created_at?: Date;
}

export interface FirebaseCustomClaims {
  user_id: string;
  grants: string[];
}

// ─── Chatbot domain types ───────────────────────────────────────────────────

export interface Tenant {
  id: string;
  name: string;
  wa_phone_number_id: string;
  wa_verify_token: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Chatbot {
  id: string;
  tenant_id: string;
  system_prompt: string;
  model: "gpt-4o" | "gpt-4o-mini" | "gpt-4-turbo" | "gpt-terra";
  temperature: number;
  max_tokens: number;
  max_history: number;
  flow_id: string | null;
  created_at: string;
  updated_at: string;
}

// Flow step transition
export type StepTransition =
  | { next_step_id: string }
  | { end_flow: true; message: string }
  | { route_to_flow_id: string };

export type FlowStepType =
  | "collect"
  | "decision"
  | "tool_call"
  | "action"
  | "route"
  | "message"
  | "end";

export interface CollectStep {
  id: string;
  type: "collect";
  field_key: string;
  field_type: "text" | "number" | "date" | "boolean" | "choice";
  question: string;
  validation_regex?: string;
  choices?: string[];
  required: boolean;
  error_message?: string;
  next_step_id?: string;
  max_iterations?: number;
  conversation_flexibility?: "low" | "medium" | "high";
  order: number;
}

export interface DecisionStep {
  id: string;
  type: "decision";
  field_key: string;
  operator: ">" | "<" | ">=" | "<=" | "==" | "!=" | "in" | "not_in";
  value: string | number | boolean | (string | number)[];
  on_true: StepTransition;
  on_false: StepTransition;
  order: number;
}

export interface ToolCallStep {
  id: string;
  type: "tool_call";
  tool_name: string;
  input_map: Record<string, string>;
  output_field: string;
  next_step_id?: string;
  order: number;
}

export interface ActionStep {
  id: string;
  type: "action";
  tool_name: string;
  input_map: Record<string, string>;
  next_step_id?: string;
  order: number;
}

export interface RouteStep {
  id: string;
  type: "route";
  next_flow_id: string;
  order: number;
}

export interface MessageStep {
  id: string;
  type: "message";
  content: string;
  next_step_id?: string;
  order: number;
}

export interface EndStep {
  id: string;
  type: "end";
  content: string;
  order: number;
}

export type FlowStep =
  | CollectStep
  | DecisionStep
  | ToolCallStep
  | ActionStep
  | RouteStep
  | MessageStep
  | EndStep;

export interface Flow {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  channel: "whatsapp" | "*";
  first_step_id: string;
  steps: FlowStep[];
  webhook_url?: string;
  created_at: string;
  updated_at: string;
}

// ─── EGG Gateway domain types ───────────────────────────────────────────────

export const EGG_KNOWN_ACTIONS = [
  "lead.create.pre",
  "lead.create.post",
  "lead.update.pre",
  "campaign.lead.update.pre",
  "qualification.start",
  "qualification.end",
  "qualification.pending",
  "qualification.abort",
] as const;

// Derivato da EGG_KNOWN_ACTIONS: fonte unica, nessuna divergenza da mantenere a mano.
export type EggEventType = (typeof EGG_KNOWN_ACTIONS)[number];

export type EggConditionOperator =
  | "=="
  | "!="
  | ">"
  | "<"
  | ">="
  | "<="
  | "in"
  | "not_in";

export interface EggCondition {
  field: string;
  operator: EggConditionOperator;
  value: string | number | boolean | string[];
}

export interface EggChatbotTarget {
  tenant_id: string;
  flow_id: string;
  phone_field: string;
}

export interface EggWebhookTarget {
  url: string;
  headers?: Record<string, string>;
  http_method?: "POST" | "PATCH" | "PUT";
}

export interface EggFinanceUpdateTarget {
  entity: string; // default "lead"
  entity_id_field: string; // dot-path risolto contro payload.data
  http_method?: "PATCH" | "PUT"; // default PATCH
  x_egg_user?: string;
}

// Parametro posizionale di un template WhatsApp: la posizione nell'array è
// vincolante — [0] riempie {{1}}, [1] riempie {{2}}, ecc.
export type EggTemplateParameter =
  | { type: "static"; value: string | number | boolean }
  | {
      type: "dynamic";
      source: "data" | "newData" | "oldData"; // root di payload.data
      field: string; // dot-path, anche nested
    };

export interface EggWhatsAppTemplate {
  parameters_path: string; // dot-path nel body outbound
  parameters: EggTemplateParameter[]; // ordinati
}

export interface EggRoutingRule {
  id: string;
  name: string;
  active: boolean;
  priority: number;
  event_types: string[];
  match_mode: "all" | "any";
  conditions: EggCondition[];
  target_type: "chatbot_flow" | "external_webhook" | "egg_finance_update";
  target_chatbot_flow?: EggChatbotTarget;
  target_external_webhook?: EggWebhookTarget;
  target_egg_finance_update?: EggFinanceUpdateTarget;
  mapping?: Record<string, string>;
  mapping_mode?: "replace" | "merge"; // assente = replace (solo campi mappati)
  extra_fields?: Record<string, string>;
  whatsapp_template?: EggWhatsAppTemplate; // solo canale WhatsApp (external_webhook)
  created_at: string;
  updated_at: string;
}

export type EggEventStatus = "dispatched" | "no_rule" | "error" | "duplicate";

export interface EggHttpCallLog {
  request: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body: Record<string, unknown>;
  };
  response?: {
    status_code: number;
    body: string;
    latency_ms: number;
    truncated?: boolean;
  };
  error?: string;
}

export interface EggEventLog {
  id: string;
  event_id?: string;
  action?: string;
  company?: string;
  rule_id: string | null;
  rule_name?: string | null;
  target_type: string | null;
  status: EggEventStatus;
  error?: string;
  raw_payload?: Record<string, unknown>;
  candidate_rule_ids?: string[];
  http_call?: EggHttpCallLog;
  // chatbot_flow targets only (see BE T-017) — enable deep-link to the execution.
  tenant_id?: string;
  flow_id?: string;
  user_phone?: string;
  trigger_message_id?: string;
  created_at: string;
}

export interface EggEventLogsResponse {
  logs: EggEventLog[];
  next_cursor?: string;
}

// ─── Webhook Keys ────────────────────────────────────────────────────────────

export type WebhookKeyStatus = "active" | "disabled" | "revoked";

export interface WebhookKey {
  id: string;
  partnerId: string;
  partnerName: string;
  keyPrefix: string;
  status: WebhookKeyStatus;
  scopes?: string[];
  allowedEventTypes?: string[];
  createdAt: string;
  createdBy: string;
  lastUsedAt?: string;
  lastUsedIp?: string;
  revokedAt?: string;
  revokedBy?: string;
  rotationOf?: string;
  notes?: string;
}

export interface WebhookKeyWithPlain extends WebhookKey {
  keyPlain: string;
}

export type WebhookKeyAuditOp =
  | "created"
  | "disabled"
  | "enabled"
  | "revoked"
  | "rotated"
  | "updated";

export interface KeyAuditOperation {
  id: string;
  keyId: string;
  partnerId: string;
  operation: WebhookKeyAuditOp;
  actor_uid: string;
  actor_email?: string;
  meta?: Record<string, unknown>;
  created_at: string;
}

// ─── Monitoring ──────────────────────────────────────────────────────────────

// Monitoring
export interface CollectedData {
  tenant_id: string;
  user_phone: string;
  flow_id: string;
  data: Record<string, unknown>;
  completed_at: { seconds: number } | null;
}

export interface Conversation {
  tenant_id: string;
  user_phone: string;
  updated_at: { seconds: number };
  messages: { role: string; content: string }[];
  flow_state?: {
    flow_id: string;
    current_step_id: string;
    status: "active" | "completed" | "failed";
    collected_data: Record<string, unknown>;
    // Populated by the flow runtime (see BE T-017) — absent on older conversations.
    last_error?: { code: string; step_id: string | null; message: string };
    trigger?: {
      source: string;
      internal_event_id: string;
      event_id?: string;
      rule_id: string;
    };
    step_history?: { step_id: string; type: string }[];
  } | null;
}

// ─── Lead Dispatching domain types ──────────────────────────────────────────
// Riferimento: docs/FE_LEAD_DISPATCH_HANDOFF.md §3

export type RuleType = "simple" | "dispatcher";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface LeadDispatchRuleBase {
  id: string;
  displayName: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SimpleRuleDestination {
  url: string;
  method: HttpMethod;
  headers?: Record<string, string>;
  extra_fields?: Record<string, string | number | boolean>;
  timeout_ms?: number;
}

export interface SimpleRule extends LeadDispatchRuleBase {
  type: "simple";
  destination: SimpleRuleDestination;
  mapping?: Record<string, string>;
}

export interface DispatcherTarget {
  simpleRuleId: string;
  percentage: number;
}

export type DispatcherDayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface DispatcherSchedule {
  dayOfWeek: DispatcherDayOfWeek;
  startHour: number;
  endHour: number;
  targets: DispatcherTarget[];
}

export interface DispatcherFallback {
  simpleRuleId: string;
}

/**
 * Target selection strategy for dispatcher rules.
 *   - "weighted": probabilistic pick (default, legacy behaviour)
 *   - "observe":  selection stays weighted, daily counter accrues + divergence logged (no impact)
 *   - "enforce":  deterministic deficit-based selection honours the percentages exactly
 */
export type SelectionStrategy = "weighted" | "observe" | "enforce";

export interface DispatcherRule extends LeadDispatchRuleBase {
  type: "dispatcher";
  timezone: string;
  schedules: DispatcherSchedule[];
  fallback?: DispatcherFallback | null;
  selection_strategy?: SelectionStrategy;
}

export type Rule = SimpleRule | DispatcherRule;

export type RuleCreateInput = Omit<Rule, "id" | "created_at" | "updated_at">;
export type RuleUpdateInput = Partial<RuleCreateInput>;

export type LeadDispatchStatus =
  | "dispatched"
  | "duplicate"
  | "skipped"
  | "error"
  | "failed";

export interface LeadDispatchHttpCallRequest {
  url: string;
  method: HttpMethod;
  headers: Record<string, string>;
  body: string;
}

export interface LeadDispatchHttpCallResponse {
  status_code: number;
  body: string;
  truncated: boolean;
  latency_ms: number;
}

export interface LeadDispatchLog {
  id: string;
  created_at: string;
  expires_at: string;

  request_id: string;
  api_key_id?: string;
  partner_id?: string;

  lead_id?: string;
  external_ref?: string;

  rule_id: string;
  rule_type: RuleType;
  dispatcher_rule_id?: string;
  simple_rule_id?: string;

  destination_url?: string;
  destination_method?: HttpMethod;

  status: LeadDispatchStatus;
  http_status?: number;
  error_code?: string;
  error_message?: string;

  payload_snapshot?: Record<string, unknown>;
  response_snapshot?: { body: string; truncated: boolean };

  http_call?: {
    request: LeadDispatchHttpCallRequest;
    response?: LeadDispatchHttpCallResponse;
    error?: string;
  };

  duration_ms?: number;
  attempt?: number;
}

export interface LeadDispatchLogsResponse {
  logs: LeadDispatchLog[];
  nextCursor?: string;
}

export interface DryRunOutboundPreview {
  selected_simple_rule_id: string;
  url: string;
  method: HttpMethod;
  headers: Record<string, string>;
  body: Record<string, unknown>;
}

export type DryRunResponse =
  | { ok: true; request_id: string; status: "dry-run"; outbound_preview: DryRunOutboundPreview }
  | { ok: true; request_id: string; status: "skipped"; reason: string };
